import { elementSymbol } from './elements.ts';

// Measured widths for the fixed Arial/sans-serif, 600-weight, 17px stack every atom label uses
// (src/rendering/svgExport.ts's <g font-family="Arial, sans-serif" font-size="17" font-weight=
// "600">, matched by .atom-labels text in src/styles.css). Measured via SVGTextElement.getBBox()
// in headless Chrome -- more faithful than Canvas measureText() since it measures the exact SVG
// text-rendering path production uses -- rather than a runtime measurement call, because
// scripts/test-graph-model.mts runs drawingDocumentToSvg under plain Node with no DOM/Canvas
// available. A static table rather than a generate:* script: Arial's own metrics don't change,
// so there's no upstream source this would ever need to regenerate from.
//
// Every symbol shares an identical measured height (19px, no descenders in any element symbol),
// so unlike width there's no need for a per-symbol height table.
const ELEMENT_SYMBOL_WIDTH: Readonly<Record<string, number>> = {
  H: 12.28,
  He: 21.73,
  Li: 15.11,
  Be: 21.73,
  B: 12.28,
  C: 12.28,
  N: 12.28,
  O: 13.23,
  F: 10.39,
  Ne: 21.73,
  Na: 21.73,
  Mg: 24.55,
  Al: 17,
  Si: 16.06,
  P: 11.34,
  S: 11.34,
  Cl: 17,
  Ar: 19.11,
  K: 12.28,
  Ca: 21.73,
  Sc: 20.8,
  Ti: 14.81,
  V: 11.35,
  Cr: 19.11,
  Mn: 24.55,
  Fe: 19.84,
  Co: 22.67,
  Ni: 17,
  Cu: 22.67,
  Zn: 20.78,
  Ga: 22.69,
  Ge: 22.69,
  As: 21.73,
  Se: 20.8,
  Br: 19.11,
  Kr: 19.11,
  Rb: 22.67,
  Sr: 18.17,
  Y: 11.38,
  Zr: 17.22,
  Nb: 22.67,
  Mo: 24.55,
  Tc: 18.58,
  Ru: 22.67,
  Rh: 22.67,
  Pd: 21.73,
  Ag: 22.67,
  Cd: 22.67,
  In: 15.11,
  Sn: 21.73,
  Sb: 21.73,
  Te: 18.58,
  I: 4.73,
  Xe: 20.8,
  Cs: 21.73,
  Ba: 21.73,
  La: 19.84,
  Ce: 21.73,
  Pr: 18.17,
  Nd: 22.67,
  Pm: 26.47,
  Sm: 26.47,
  Eu: 21.73,
  Gd: 23.61,
  Tb: 20.78,
  Dy: 21.73,
  Ho: 22.67,
  Er: 18.17,
  Tm: 25.5,
  Yb: 21.76,
  Lu: 20.78,
  Hf: 18.44,
  Ta: 18.58,
  W: 16.05,
  Re: 21.73,
  Os: 22.69,
  Ir: 11.55,
  Pt: 17,
  Au: 22.67,
  Hg: 22.67,
  Tl: 15.11,
  Pb: 21.73,
  Bi: 17,
  Po: 21.73,
  At: 17.95,
  Rn: 22.67,
  Fr: 17.22,
  Ra: 21.73,
  Ac: 21.73,
  Th: 20.78,
  Pa: 20.8,
  U: 12.28,
  Np: 22.67,
  Pu: 21.73,
  Am: 27.41,
  Cm: 27.41,
  Bk: 21.73,
  Cf: 18.44,
  Es: 20.8,
  Fm: 25.5,
  Md: 24.55,
  No: 22.67,
  Lr: 17.22,
  Rf: 18.44,
  Db: 22.67,
  Sg: 21.73,
  Bh: 22.67,
  Hs: 21.73,
  Mt: 19.83,
  Ds: 21.73,
  Rg: 22.67,
  Cn: 22.67,
  Nh: 22.67,
  Fl: 15.11,
  Mc: 23.62,
  Lv: 19.84,
  Ts: 18.58,
  Og: 23.61
};

// Fallback for an atomicNumber outside the measured table (elementSymbol() itself falls back to
// "[n]" for anything past 118) -- a generous estimate rather than a crash.
const FALLBACK_SYMBOL_WIDTH = 24;

const SUPERSCRIPT_DIGIT_WIDTH: Readonly<Record<string, number>> = {
  '0': 4.73,
  '1': 5.67,
  '2': 5.67,
  '3': 5.67,
  '4': 4.73,
  '5': 4.73,
  '6': 4.73,
  '7': 4.73,
  '8': 4.73,
  '9': 4.73
};

const HYDROGEN_LABEL_WIDTH = 12.28; // "H" alone
// Arimo's digits are tabular: every subscript digit has the same advance.
const SUBSCRIPT_DIGIT_WIDTH = 4.73;

export const MAIN_LABEL_GLYPH_HEIGHT = 19;

// A small margin around the measured ink box (#389 halved it from 3px). Bonds stop at the letter
// shapes themselves since #965 (labelOutline.ts); the box now only sizes a label's extent.
export const LABEL_MASK_PADDING_PX = 1.5;

function isotopeSuperscriptWidth(isotope: number | undefined): number {
  if (isotope === undefined) return 0;
  return String(isotope)
    .split('')
    .reduce((sum, digit) => sum + (SUPERSCRIPT_DIGIT_WIDTH[digit] ?? 0), 0);
}

// Element symbol + isotope superscript prefix, exactly the text isotopeSuperscript(...) +
// elementSymbol(...) renders as one run in svgExport.ts/DrawingPaper.tsx. Widths are summed
// rather than measured per-combination (a combinatorial explosion for no real gain -- measuring
// "H" immediately followed by a subscript showed summing individual widths is accurate to within
// a fraction of a pixel, well inside the padding budget).
// #648: free-text labels (Markush `R1`, `[C,N,O,S]`) have no per-glyph table, so estimate from
// character class at the main label's font size: an uppercase letter is as wide as a one-letter
// element symbol ("C"/"N"), anything else as wide as an element symbol's lowercase second letter.
const UPPERCASE_GLYPH_WIDTH = 12.28;
const OTHER_GLYPH_WIDTH = 9.5;

export function textLabelWidth(text: string): number {
  return [...text].reduce(
    (width, glyph) => width + (/[A-Z]/.test(glyph) ? UPPERCASE_GLYPH_WIDTH : OTHER_GLYPH_WIDTH),
    0
  );
}

export function atomSymbolLabelWidth(atomicNumber: number, isotope: number | undefined): number {
  return (
    isotopeSuperscriptWidth(isotope) + (ELEMENT_SYMBOL_WIDTH[elementSymbol(atomicNumber)] ?? FALLBACK_SYMBOL_WIDTH)
  );
}

// The "H"/"H2"/"H4"-with-subscript text implicitHydrogenLabel(count) renders.
export function implicitHydrogenLabelWidth(count: number): number {
  if (count <= 1) return HYDROGEN_LABEL_WIDTH;
  return HYDROGEN_LABEL_WIDTH + String(count).length * SUBSCRIPT_DIGIT_WIDTH;
}

export interface AtomLabelBox {
  readonly maskX: number;
  readonly maskY: number;
  readonly maskWidth: number;
  readonly maskHeight: number;
}

// The one predicate for "this atom draws a visible symbol" (rather than an implied vertex): a
// bonded carbon at neutral charge with no isotope / radical / resonance annotation is left bare;
// a carbon with no bonds has no lines to imply it, so it shows (#1068). Shared
// by the live canvas and SVG export so their label set - and the bond trimming below - can't
// drift apart.
export function atomShowsLabel(atom: {
  readonly atomicNumber: number;
  readonly formalCharge: number;
  readonly isotope?: number;
  readonly radicalElectronCount?: number;
  readonly resonanceCharge?: number;
  readonly labelOverride?: string;
  readonly isolated?: true;
}): boolean {
  return (
    atom.isolated === true ||
    atom.labelOverride !== undefined ||
    atom.atomicNumber !== 6 ||
    atom.formalCharge !== 0 ||
    atom.isotope !== undefined ||
    atom.radicalElectronCount !== undefined ||
    atom.resonanceCharge !== undefined
  );
}

// The single shared implementation of the label-occlusion-mask box, used by both svgExport.ts
// (export) and DrawingPaper.tsx (live editing canvas) -- previously each reimplemented this
// with its own copy of the same fixed-heuristic constants, which is how the two could drift.
// Sizes the box from the *measured* label width/height above (see this module's header comment
// for why a static table rather than a runtime measurement) instead of a fixed guess, with
// LABEL_MASK_PADDING_PX of padding around the ink box on every side. When an implicit-hydrogen
// suffix is present, the box is still the union of the symbol's box and the hydrogen label's box
// (needed for correctness -- the mask must fully occlude the bond line behind a multi-part
// label), just built from real half-widths instead of flat 11/15px constants.
export function atomLabelBox(
  atomicNumber: number,
  isotope: number | undefined,
  x: number,
  y: number,
  hydrogenX: number | undefined,
  hydrogenY: number | undefined,
  implicitHydrogenCount: number,
  scale: number,
  padding: number = LABEL_MASK_PADDING_PX,
  labelOverride?: string
): AtomLabelBox {
  const symbolWidth =
    labelOverride === undefined ? atomSymbolLabelWidth(atomicNumber, isotope) : textLabelWidth(labelOverride);
  const symbolHalfWidth = (symbolWidth / 2 + padding) * scale;
  const glyphHalfHeight = (MAIN_LABEL_GLYPH_HEIGHT / 2 + padding) * scale;
  if (hydrogenX === undefined || hydrogenY === undefined) {
    return {
      maskX: x - symbolHalfWidth,
      maskY: y - glyphHalfHeight,
      maskWidth: symbolHalfWidth * 2,
      maskHeight: glyphHalfHeight * 2
    };
  }
  const hydrogenHalfWidth = (implicitHydrogenLabelWidth(implicitHydrogenCount) / 2 + padding) * scale;
  const maskX = Math.min(x - symbolHalfWidth, hydrogenX - hydrogenHalfWidth);
  const maskY = Math.min(y, hydrogenY) - glyphHalfHeight;
  return {
    maskX,
    maskY,
    maskWidth: Math.max(x + symbolHalfWidth, hydrogenX + hydrogenHalfWidth) - maskX,
    maskHeight: Math.abs(hydrogenY - y) + glyphHalfHeight * 2
  };
}
